'use client';
import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const trustBadges = [
  { icon: 'ShieldCheckIcon', label: 'Enterprise Security', desc: 'Bank-grade encryption' },
  { icon: 'CheckBadgeIcon', label: 'GDPR Compliant', desc: 'Data privacy first' },
  { icon: 'StarIcon', label: '4.8/5 Rating', desc: 'From 47+ reviews' },
  { icon: 'UserGroupIcon', label: '6 Partner Companies', desc: 'Trusted operators' },
  { icon: 'ClockIcon', label: '99.9% Uptime', desc: 'SLA guaranteed' },
  { icon: 'LockClosedIcon', label: 'ISO 27001 Ready', desc: 'Security standards' },
];

const testimonials = [
  {
    name: 'Rajesh',
    role: 'Founder, MayaCallTaxi',
    location: 'Vellore, Tamil Nadu',
    quote: 'Maya Call Taxi initially operated across Vellore, Ambur, Ranipet, Kanchipuram, Arani, and Chennai. After implementing Movio, our operations became more efficient, enabling us to expand to Cuddalore, Tirupathur, Vaniyambadi, and Tindivanam, while also successfully launching our services in a new state, Puducherry, within just three months. Movio has been a valuable partner in our growth journey.',
    rating: 5,
    metric: '4 New Cities • 1 New State',
    metricIcon: 'MapPinIcon',
  },
  {
    name: 'Sathish',
    role: 'Founder & CEO, FTaxi',
    location: 'Thanjavur, Tamil Nadu',
    quote: 'Two years ago, we were operating under the Friends Track brand as the Thanjavur franchise. With a vision to build our own identity, we launched FTaxi using Movio platform. The software helped us manage our operations smoothly and scale our business efficiently. Today, FTaxi has expanded across 8 cities, with 3 more cities set to launch soon. Our monthly trips have grown from around 11,000 to over 70,000. We highly recommend Movio to any taxi operator looking to build and grow their own brand.',
    rating: 5,
    metric: '8 Cities • 70K+ Monthly Trips',
    metricIcon: 'ChartBarIcon',
  },
  {
    name: 'Ganesh',
    role: 'Franchise Owner, FTaxi',
    location: 'Kovilpatti, Tamil Nadu',
    quote: 'Being one of the FTaxi franchise owners in Kovilpatti, we had already built a stable and successful business. With Movio, we gained better control over our bookings and day-to-day operations, helping us achieve a remarkable 10X increase in turnover in just nine months. The platform has become an important part of our continued success.',
    rating: 5,
    metric: '10X Turnover Growth in 9 Months',
    metricIcon: 'RocketLaunchIcon',
  },
];

const caseStudies = [
  {
    company: 'F Taxi',
    location: 'Tamil Nadu',
    challenge: 'Manual booking system, no digital presence, losing customers to app-based competitors',
    solution: 'Launched white-label taxi app with Movio in 3 weeks',
    results: ['95% increase in bookings', '40% reduction in idle time', 'Substantial monthly revenue growth'],
    icon: 'TruckIcon',
    color: '#0066FF',
  },
  {
    company: 'Thaani',
    location: 'Tamil Nadu',
    challenge: 'Inefficient routing, high fuel costs, no real-time visibility for clients',
    solution: 'Deployed Movio Logistics Platform with AI route optimization',
    results: ['30% fuel cost savings', '50% faster deliveries', '100% client visibility'],
    icon: 'ArrowPathIcon',
    color: '#075DDF',
  },
];

export default function TrustSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Auto slide on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const startAutoPlay = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % testimonials.length;
          scrollToIndex(next);
          return next;
        });
      }, 4000);
    };

    startAutoPlay();
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isMobile]);

  const scrollToIndex = (index: number) => {
    const container = containerRef.current;
    if (!container) return;
    
    const card = container.querySelector('.testimonial-card') as HTMLElement;
    if (!card) return;
    
    const cardWidth = card.offsetWidth + 24; // width + gap
    container.scrollTo({
      left: index * cardWidth,
      behavior: 'smooth',
    });
  };

  const goToSlide = (index: number) => {
    // Reset auto-play timer
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % testimonials.length;
          scrollToIndex(next);
          return next;
        });
      }, 4000);
    }
    
    setCurrentIndex(index);
    scrollToIndex(index);
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    // Pause auto-play on touch
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const container = containerRef.current;
    if (!container) return;
    
    const card = container.querySelector('.testimonial-card') as HTMLElement;
    if (!card) return;
    
    const cardWidth = card.offsetWidth + 24;
    const scrollPosition = container.scrollLeft;
    const newIndex = Math.round(scrollPosition / cardWidth);
    const clampedIndex = Math.min(Math.max(newIndex, 0), testimonials.length - 1);
    
    setCurrentIndex(clampedIndex);
    scrollToIndex(clampedIndex);
    
    // Resume auto-play
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % testimonials.length;
        scrollToIndex(next);
        return next;
      });
    }, 4000);
  };

  return (
    <>
      {/* Trust Badges */}
      <section className="py-16 bg-white border-b border-[rgba(15,23,42,0.06)]" aria-label="Trust and security badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 animate-on-scroll stagger-1">
            <p className="text-sm font-semibold text-[#64748B] uppercase tracking-wider">Trusted by operators across India</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {trustBadges.map((badge, i) => (
              <div
                key={badge.label}
                className={`animate-on-scroll stagger-${i + 1} flex flex-col items-center text-center p-5 rounded-2xl bg-[#FFFFFF] border border-[rgba(15,23,42,0.06)] hover:border-[#0066FF] hover:bg-[rgba(0,102,255,0.03)] transition-all duration-300`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-[rgba(0,102,255,0.08)]">
                  <Icon
                    name={badge.icon as Parameters<typeof Icon>[0]['name']}
                    size={20}
                    className="text-[#0066FF]"
                  />
                </div>
                <div className="text-sm font-semibold text-[#0F172A] mb-0.5">{badge.label}</div>
                <div className="text-xs text-[#64748B]">{badge.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - With Dots Navigation on Mobile */}
      <section id="testimonials" className="py-20 bg-[#F8FAFC] scroll-mt-24 md:scroll-mt-28" aria-label="Customer testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll stagger-1">
            <span className="inline-block text-sm font-semibold text-[#0066FF] bg-[rgba(0,102,255,0.08)] px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">What Operators Say</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              What Operators{' '}
              <span className="text-[#0066FF]">Say About Movio.</span>
            </h2>
            <p className="mt-4 text-[#64748B] text-lg max-w-2xl mx-auto">
              Real operators. Real growth. See how Movio is powering mobility and delivery businesses across India.
            </p>
          </div>

          {/* Desktop: Grid Layout */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className={`animate-on-scroll stagger-${i + 2} group bg-white rounded-2xl p-6 border border-[rgba(15,23,42,0.06)] shadow-sm hover:shadow-xl transition-all duration-300 hover:border-[#0066FF] hover:bg-[rgba(0,102,255,0.02)]  flex flex-col h-full relative overflow-hidden`}
                onClick={() => {
                  const el = document.getElementById(`testimonial-${t.name}`);
                  if (el) {
                    el.style.transition = 'background-color 0.15s';
                    el.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                    setTimeout(() => {
                      el.style.backgroundColor = '';
                    }, 300);
                  }
                }}
                id={`testimonial-${t.name}`}
              >
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-[rgba(0,102,255,0.03)] to-transparent"></div>
                </div>

                <div className="flex items-start gap-4 mb-4 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#0066FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0 transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg group-hover:shadow-[rgba(0,102,255,0.3)]">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A] text-base group-hover:text-[#0066FF] transition-colors duration-300">{t.name}</div>
                    <div className="text-sm text-[#475569]">{t.role}</div>
                    <div className="text-xs text-[#64748B]">{t.location}</div>
                  </div>
                </div>

                <div className="flex gap-1 mb-4 relative z-10">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Icon
                      key={j}
                      name="StarIcon"
                      size={16}
                      className="text-amber-400 fill-amber-400 transition-all duration-300 group-hover:scale-110"
                    />
                  ))}
                </div>

                <blockquote className="text-[#475569] leading-relaxed text-sm flex-1 relative z-10 group-hover:text-[#0F172A] transition-colors duration-300">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <div className="mt-6 pt-4 border-t border-[rgba(15,23,42,0.06)] relative z-10">
                  <div className="bg-[rgba(0,102,255,0.06)] rounded-xl px-4 py-3 flex items-center gap-3 transition-all duration-300 group-hover:bg-[rgba(0,102,255,0.12)] group-hover:border group-hover:border-[rgba(0,102,255,0.2)]">
                    <div className="w-8 h-8 rounded-lg bg-[#0066FF] flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:shadow-[rgba(0,102,255,0.3)]">
                      <Icon 
                        name={t.metricIcon as Parameters<typeof Icon>[0]['name']} 
                        size={16} 
                        className="text-white" 
                      />
                    </div>
                    <span className="text-sm font-semibold text-[#0F172A] group-hover:text-[#0066FF] transition-colors duration-300">{t.metric}</span>
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0066FF] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            ))}
          </div>

          {/* Mobile: Carousel with Dots */}
          <div className="md:hidden">
            <div 
              ref={containerRef}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 hide-scrollbar"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {testimonials.map((t, index) => (
                <div
                  key={`mobile-${t.name}`}
                  className="testimonial-card min-w-[85%] max-w-[85%] flex-shrink-0 snap-center bg-white rounded-2xl p-6 border border-[rgba(15,23,42,0.06)] shadow-sm flex flex-col relative overflow-hidden"
                  onClick={() => {
                    // Click effect
                    const el = document.getElementById(`mobile-card-${t.name}`);
                    if (el) {
                      el.style.transition = 'background-color 0.15s';
                      el.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                      setTimeout(() => {
                        el.style.backgroundColor = '';
                      }, 300);
                    }
                    // Go to this slide
                    goToSlide(index);
                  }}
                  id={`mobile-card-${t.name}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#0066FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A] text-base">{t.name}</div>
                      <div className="text-sm text-[#475569]">{t.role}</div>
                      <div className="text-xs text-[#64748B]">{t.location}</div>
                    </div>
                  </div>

                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Icon
                        key={j}
                        name="StarIcon"
                        size={16}
                        className="text-amber-400 fill-amber-400"
                      />
                    ))}
                  </div>

                  <blockquote className="text-[#475569] leading-relaxed text-sm flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>

                  <div className="mt-6 pt-4 border-t border-[rgba(15,23,42,0.06)]">
                    <div className="bg-[rgba(0,102,255,0.06)] rounded-xl px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#0066FF] flex items-center justify-center flex-shrink-0">
                        <Icon 
                          name={t.metricIcon as Parameters<typeof Icon>[0]['name']} 
                          size={16} 
                          className="text-white" 
                        />
                      </div>
                      <span className="text-sm font-semibold text-[#0F172A]">{t.metric}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentIndex === index 
                      ? 'w-8 bg-[#0066FF]' 
                      : 'w-2 bg-[#CBD5E1] hover:bg-[#94A3B8]'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies - YOUR ORIGINAL DESIGN RESTORED */}
      <section className="py-20 bg-white" aria-label="Case studies">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll stagger-1">
            <span className="inline-block text-sm font-semibold text-[#0066FF] bg-[rgba(0,102,255,0.08)] px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">Case Studies</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Real Results.{' '}
              <span className="text-[#0066FF]">Real Operators.</span>
            </h2>
            <p className="mt-4 text-[#64748B] text-lg max-w-2xl mx-auto">
              See how businesses like yours achieved remarkable growth with Movio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((cs, i) => (
              <div
                key={cs.company}
                className={`animate-on-scroll stagger-${i + 2} bg-[#FFFFFF] rounded-2xl border border-[rgba(15,23,42,0.08)] overflow-hidden hover:shadow-lg transition-all duration-300`}
              >
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${cs.color}15` }}>
                      <Icon name={cs.icon as Parameters<typeof Icon>[0]['name']} size={24} style={{ color: cs.color }} />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0F172A] text-lg">{cs.company}</div>
                      <div className="text-sm text-[#64748B]">{cs.location}</div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-1">Challenge</div>
                      <p className="text-sm text-[#475569]">{cs.challenge}</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-[#0066FF] uppercase tracking-wider mb-1">Solution</div>
                      <p className="text-sm text-[#475569]">{cs.solution}</p>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3">Results</div>
                    <div className="flex flex-col gap-2">
                      {cs.results.map((result) => (
                        <div key={result} className="flex items-center gap-2">
                          <Icon name="CheckCircleIcon" size={16} className="text-emerald-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-[#0F172A]">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 animate-on-scroll stagger-4">
            <Link 
              href="/demo-booking" 
              className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold bg-[#0066FF] text-white hover:bg-[#075DDF] transition-all duration-300"
            >
              Get Similar Results
              <Icon name="ArrowRightIcon" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Custom CSS */}
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .testimonial-card {
          scroll-snap-align: center;
        }
      `}</style>
    </>
  );
}